import React, { forwardRef, useMemo, useRef } from "react";
import {
  Button,
  CloseIcon,
  Dialog,
  Divider,
  EmailIcon,
  FilesPdfIcon,
  Flex,
  Text,
} from "@fluentui/react-northstar";
import "./EvaluationReportDialog.scss";
import moment from "moment";
import { Col, Progress, Row } from "antd";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import { useIntelligentGrading } from "../../../../../context/useIntelligentGrading";
import EvaluationReportDialogContent from "./EvaluationReportDialogContent";

const EvaluationReportDialog = forwardRef(
  (
    {
      open,
      onClose,
      gradingResult,
      rubrics,
      studentName,
      sendGradingReport,
      type,
    },
    ref
  ) => {
    const { isSendingReport } = useIntelligentGrading();
    const { t } = useTranslation();
    const translation = t("assignment").summary.single;

    const attachmentTypes = [
      {
        type: "text",
        label: translation.evaluationReport.attachmentTypes.textResponse,
      },
      {
        type: "voice",
        label: translation.evaluationReport.attachmentTypes.audioRecording,
      },
      {
        type: "video",
        label: translation.evaluationReport.attachmentTypes.videoSubmission,
      },
      {
        type: "file",
        label: translation.evaluationReport.attachmentTypes.fileAttachments,
      },
      {
        type: "link",
        label: translation.evaluationReport.attachmentTypes.researchLinks,
      },
    ];

    const handleExportPDF = async () => {
      try {
        if (!ref.current) {
          console.error("Content ref not found");
          return;
        }
        const canvas = await html2canvas(ref.current, {
          useCORS: true,
          scale: 4,
        });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 190;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
        pdf.save("evaluation-report.pdf");
      } catch (error) {
        console.error("Error generating PDF:", error);
      }
    };

    function getPerformanceLabelFromScore(score) {
      if (score === 0)
        return translation.evaluationReport.performanceLabels.notSubmitted;
      if (score >= 90)
        return `${translation.evaluationReport.performanceLabels.excellent} (${score}%)`;
      if (score >= 80)
        return `${translation.evaluationReport.performanceLabels.veryGood} (${score}%)`;
      if (score >= 70)
        return `${translation.evaluationReport.performanceLabels.good} (${score}%)`;
      if (score >= 60)
        return `${translation.evaluationReport.performanceLabels.fair} (${score}%)`;
      if (score >= 40)
        return `${translation.evaluationReport.performanceLabels.needsImprovement} (${score}%)`;
      return `${translation.evaluationReport.performanceLabels.poor} (${score}%)`;
    }

    const formatAnalysis = useMemo(() => {
      const formatAnalysis = attachmentTypes.map((item) => {
        const score = gradingResult?.formatAnalysis[item.type] || 0;
        return {
          ...item,
          score,
          performanceLable: getPerformanceLabelFromScore(score),
        };
      });
      return formatAnalysis;
    }, [gradingResult, attachmentTypes]);

    return (
      <Dialog
        open={open}
        header={translation.evaluationReport.title}
        className="evaluation-report-dialog"
        content={
          <EvaluationReportDialogContent
            ref={ref}
            gradingResult={gradingResult}
            rubrics={rubrics}
            studentName={studentName}
            sendGradingReport={sendGradingReport}
            type={type}
            onClose={onClose}
            isSendingReport={isSendingReport}
            translation={translation}
            handleExportPDF={handleExportPDF}
          />
        }
        headerAction={{
          icon: <CloseIcon />,
          title: translation.evaluationReport.close,
          onClick: () => {
            onClose();
          },
        }}
      />
    );
  }
);

export default EvaluationReportDialog;
